import type { InputProps } from '@/components/_ui/Input'
import { Input } from '@/components/_ui/Input'
import useGlobalStore from '@/store/useGlobalStore'
import React, { useEffect, useRef, useState } from 'react'
import { Button } from '@/components/_ui/Button'
import Image from 'next/image'
import { PasswordRequirements } from './PasswordRequirements'
import { getAllPasswordRequirements } from './_helpers/checkPasswordRequirements'

type PasswordInputProps = InputProps & {
  withPasswordReveal?: boolean
  withPasswordRequirements?: boolean
  onPasswordRequirementsError?: (error: boolean) => void
}

export type PasswordRequirementType = {
  validated: boolean
  error: string
}

const PasswordInput = (
  {
    ref,
    withPasswordRequirements,
    withPasswordReveal,
    onPasswordRequirementsError = () => {},
    ...props
  }: PasswordInputProps,

  // eslint-disable-next-line sonarjs/cognitive-complexity
) => {
  const {
    app: { marketExceptions },
  } = useGlobalStore()

  const inputRef = useRef<HTMLInputElement | null>(null)

  const [passwordValue, setPasswordValue] = useState('')
  const [passwordRequirements, setPasswordRequirements] = useState<
    PasswordRequirementType[] | null
  >([])
  const [showPassword, setShowPassword] = useState(false)

  useEffect(() => {
    function areAllValidated(objects: PasswordRequirementType[]): boolean {
      // Use the every method to check if all objects have validated === true
      return objects.every((object) => object.validated)
    }

    if (marketExceptions?.showPasswordRequirements) {
      const errors = getAllPasswordRequirements({ value: passwordValue })
      if (errors.length > 0) {
        if (areAllValidated(errors) || passwordValue.length === 0) {
          onPasswordRequirementsError(false)
          setPasswordRequirements(null)
        } else if (!areAllValidated(errors) && passwordValue.length > 0) {
          onPasswordRequirementsError(true)
          setPasswordRequirements(errors)
        }
      }
    }
  }, [marketExceptions?.showPasswordRequirements, passwordValue])

  useEffect(() => {
    const node = inputRef.current
    const listen = (data: unknown): void => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
      setPasswordValue(data?.target?.value)
    }

    if (node) {
      node.addEventListener('input', listen)
      return () => {
        node.removeEventListener('input', listen)
      }
    }

    return () => {}
  }, [])

  return (
    <div className="transition-all">
      <Input
        type={showPassword ? 'text' : 'password'}
        ref={(node) => {
          inputRef.current = node

          if (typeof ref === 'function') {
            ref(node)
          } else if (ref) {
            // TODO: Mutating object props is generally not a good practice.
            // eslint-disable-next-line no-param-reassign
            ref.current = node
          }
        }}
        iconRight={
          withPasswordReveal ? (
            <Button
              type="button"
              variant="icon"
              className="pointer-events-auto"
              onClick={() => setShowPassword(!showPassword)}
            >
              <Image
                src={
                  showPassword ? '/icons/eye-show.svg' : '/icons/eye-hide.svg'
                }
                alt="Eye"
                width={20}
                height={20}
              />
            </Button>
          ) : undefined
        }
        {...props}
      />
      {withPasswordRequirements &&
        marketExceptions?.showPasswordRequirements &&
        passwordRequirements && (
          <PasswordRequirements requirements={passwordRequirements} />
        )}
    </div>
  )
}

export default PasswordInput
